import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { buildHotspotLoginUrl } from '../../utils/voucher_qr';
import { formatVoucherDuration } from './durationUtil';

const ModernTemplate = ({ vouchers, companyLogo = '', companyName = '', hotspotGatewayIp, batchHeader}) => {
  return (
    <div className="print-container-modern print-root">
      <style>{`
        @page {
          size: A4 landscape;
          margin: 2mm;
        }

        @media print {
          /* Reset all scaling on parents so 1mm = 1mm */
          html, body { width: 100%; height: 100%; margin: 0 !important; padding: 0 !important; background: white !important; }
          html *:not(.print-root):not(.print-root *) { visibility: hidden !important; }
          .print-root, .print-root * { visibility: visible !important; }
          .print-root {
            position: absolute !important;
            left: 0 !important; top: 0 !important;
            transform: none !important;
            width: 100% !important;
          }
        }

        
        .print-container-modern {
          width: 297mm;
          margin: 0 auto;
          padding: 2mm;
          background: #f5f5f5;
          display: flex;
          flex-wrap: wrap;
          gap: 0.5mm;
          align-content: flex-start;
        }
        
        @media print {
          .print-container-modern {
            background: white;
            padding: 0;
          }
        }
        
        .modern-card {
          width: calc((100% - 4 * 0.5mm) / 5);
          height: 40mm;
          max-height: 40mm;
          min-height: 40mm;
          border: 2px solid #e5e7eb;
          border-radius: 2mm;
          padding: 0;
          box-sizing: border-box;
          background: white;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          page-break-inside: avoid;
          flex-shrink: 0;
        }
        
        .modern-bg {
          position: absolute;
          top: -30%;
          right: -30%;
          width: 140%;
          height: 160%;
          background: linear-gradient(135deg, transparent 48%, #f3f4f6 48%, #f3f4f6 52%, transparent 52%);
          z-index: 0;
          pointer-events: none;
          opacity: 0.6;
        }
        
        .modern-content {
          position: relative;
          z-index: 1;
          height: 100%;
          display: flex;
          flex-direction: column;
          padding: 2mm 2mm 0.5mm 2mm;
        }
        
        .modern-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 0.8mm;
          padding-bottom: 0.5mm;
          border-bottom: 1px solid #e5e7eb;
          position: relative;
          min-height: 16mm;
          box-sizing: border-box;
          overflow: visible;
        }
        
        .modern-number {
          position: absolute;
          top: 0;
          left: 0;
          font-size: 7pt;
          font-weight: 800;
          color: #6b7280;
          padding: 0.8mm 1.5mm;
          background: #f3f4f6;
          border-radius: 0 0 1.5mm 0;
        }
        
        .modern-logo {
          margin-bottom: 0.3mm;
          margin-top: 0.2mm;
          width: 24mm;
          height: 5mm;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .modern-logo img {
          width: 24mm;
          height: 5mm;
          max-width: 24mm;
          max-height: 5mm;
          object-fit: contain;
          display: block;
        }
        
        .modern-price {
          font-size: 12pt;
          font-weight: 900;
          line-height: 1.2;
          text-align: center;
          padding: 0.5mm 0;
          width: 100%;
          align-self: center;
          white-space: nowrap;
          box-sizing: border-box;
          display: block;
          background: transparent !important;
          border: none !important;
        }
        
        .modern-body {
          flex: 1;
          display: flex;
          gap: 2mm;
          align-items: center;
          margin-bottom: 0.5mm;
        }
        
        .modern-code-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.8mm;
        }
        
        .modern-code-label {
          font-size: 4.5pt;
          color: #6b7280;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.15mm;
        }
        
        .modern-code-value {
          font-size: 8pt;
          font-weight: 800;
          letter-spacing: 0.3mm;
          padding: 0.8mm 1mm;
          border-radius: 1mm;
          text-align: center;
          white-space: nowrap;
          box-sizing: border-box;
          width: 100%;
        }
        
        .modern-info {
          display: flex;
          flex-direction: column;
          gap: 0.5mm;
          margin-top: 1mm;
        }
        
        .modern-info-item {
          font-size: 5.5pt;
          color: #374151;
          line-height: 1.4;
          font-weight: 600;
        }
        
        .modern-info-item strong {
          font-weight: 800;
          color: #1f2937;
        }
        
        .modern-qr-section {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .modern-qr-container {
          padding: 1mm;
          background: white;
          border-radius: 1mm;
          border-width: 2px;
          border-style: solid;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .modern-footer {
          color: white;
          text-align: center;
          padding: 1.2mm 0.5mm;
          font-size: 5pt;
          font-weight: 900;
          letter-spacing: 0.15mm;
          margin: auto -2mm 0 -2mm;
          border-radius: 0 0 1.5mm 1.5mm;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 3.5mm;
        }
      `}</style>
      
      {batchHeader}
      {vouchers.map((voucher, index) => {
        const color = index % 2 === 0 ? '#3b82f6' : '#f97316';
        return (
          <div key={voucher.id || index} className="modern-card" data-voucher-card>
            <div className="modern-bg"></div>
            <div className="modern-content">
              <div className="modern-header">
                <div className="modern-number">#{index + 1}</div>
                <div className="modern-logo">
                  <img src={companyLogo || ""} alt="Logo" />
                </div>
                <div className="modern-price" style={{ color }}>
                  Rp.{(voucher.price || 0).toLocaleString('id-ID')}
                </div>
              </div>
              
              <div className="modern-body">
                <div className="modern-code-section">
                  <div className="modern-code-label">KODE VOUCHER</div>
                  <div className="modern-code-value" style={{ color, backgroundColor: `${color}10` }}>
                    {voucher.voucher_code}
                  </div>
                  <div className="modern-info">
                    <div className="modern-info-item">
                      <strong>Masa Aktif:</strong> {formatVoucherDuration(voucher)}
                    </div>
                  </div>
                </div>
                
                <div className="modern-qr-section">
                  <div className="modern-qr-container" style={{ borderColor: color }}>
                    <QRCodeSVG 
                      value={buildHotspotLoginUrl(voucher.username, voucher.password, hotspotGatewayIp)}
                      size={32}
                      level="M"
                    />
                  </div>
                </div>
              </div>
              
              <div className="modern-footer" style={{ background: color }}>
                <p>JANGAN DIBUANG SELAMA MASIH AKTIF    [!]</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ModernTemplate;
